/**
 * The Vault's Favorites tab: favourited packs, then favourited individual items.
 *
 * Two separate lists because they are two separate favourite sets in the store —
 * `favoritePacks` holds pack ids, `favoriteItems` holds ids of prompts, hooks,
 * metadata and marketing assets from inside any pack.
 */
import { Star } from 'lucide-react';
import { IconButton } from '../ui';
import { ToolButton } from '../studio';
import { c } from '../../styles/theme';
import type { VaultPack } from '../../types/domain';
import { Note, TRUNCATE, type VaultController } from './shared';

export interface VaultFavoritesTabProps {
  vault: VaultController;
  onOpenPack: (pack: VaultPack) => void;
}

export default function VaultFavoritesTab({ vault, onOpenPack }: VaultFavoritesTabProps) {
  return (
    <div>
      <Note>Favorited packs</Note>
      {vault.favoritePackList.length === 0 ? (
        <Note marginBottom={18}>No favorited packs yet.</Note>
      ) : (
        <div className="lyx-grid-2" style={{ marginBottom: 18 }}>
          {vault.favoritePackList.map((p) => (
            <ToolButton
              key={p.id}
              onClick={() => onOpenPack(p)}
              trailing={<Star size={13} color={c.accent} fill={c.accent} />}
            >
              {p.name}
            </ToolButton>
          ))}
        </div>
      )}

      <Note>Favorited items</Note>
      {vault.favoriteItemList.length === 0 ? (
        <Note marginBottom={0}>No favorited prompts, hooks, or assets yet.</Note>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {vault.favoriteItemList.map((item) => (
            <div
              key={item.id}
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: '8px 10px',
                background: c.surface1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 12, fontWeight: 500, margin: 0, ...TRUNCATE }}>
                  {item.label}
                </p>
                <p style={{ fontSize: 10, color: c.text3, margin: 0 }}>from {item.packName}</p>
              </div>
              <IconButton
                label={`Remove ${item.label} from favorites`}
                onClick={() => vault.toggleItemFavorite(item.id)}
                style={{ flexShrink: 0 }}
              >
                <Star size={13} color={c.accent} fill={c.accent} />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
